import React, { RefObject, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist/webpack.mjs';
import TextBlock from '@component/common/block/text-block';
import type { IArea, IDrawing } from '@datatype/gridgio/type';
import * as color from '@constant/color';
import { acquirePdfDocument } from '@util/pdf-document-cache';

interface IProps {
  drawing: Pick<IDrawing, 'url' | 'type'>;
  page: number;
  viewportRef: RefObject<HTMLDivElement | null>;
  rotation: number;
  cropArea: IArea | null;
  handleSizeChange: (width: number, height: number) => void;
  handlePageChange: (totalPage: number) => void;
}

const Canvas = styled.canvas`
  display: block;
`;

const getAreaBounds = (area: IArea) => {
  const x = area.point.map((point) => point.x);
  const y = area.point.map((point) => point.y);
  const left = Math.min(...x);
  const top = Math.min(...y);
  return { left, top, width: Math.max(...x) - left, height: Math.max(...y) - top };
};

const DrawingLayer: React.FC<IProps> = ({
  drawing,
  page,
  viewportRef,
  rotation,
  cropArea,
  handleSizeChange,
  handlePageChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pdfDocument, setPdfDocument] = useState<{ url: string; document: PDFDocumentProxy } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const updateViewportSize = () => {
      setViewportSize({ width: viewport.clientWidth, height: viewport.clientHeight });
    };
    updateViewportSize();
    const observer = new ResizeObserver(updateViewportSize);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [viewportRef]);

  useEffect(() => {
    setPdfDocument(null);
    setIsError(false);
    setIsLoading(true);
    handlePageChange(1);
    if (drawing.type !== 'pdf') {
      return;
    }

    let isDisposed = false;
    const pdfHandle = acquirePdfDocument(drawing.url);

    const load = async () => {
      try {
        const pdf = await pdfHandle.document;
        if (!isDisposed) {
          setPdfDocument({ url: drawing.url, document: pdf });
          handlePageChange(pdf.numPages);
        }
      } catch (error) {
        if (!isDisposed) {
          console.error('PDF drawing load failed', error);
          setIsLoading(false);
          setIsError(true);
        }
      }
    };

    void load();

    return () => {
      isDisposed = true;
      pdfHandle.release();
    };
  }, [drawing.url, drawing.type, handlePageChange]);

  useEffect(() => {
    const viewport = viewportRef.current;
    const canvas = canvasRef.current;
    if (!viewport || !canvas) return;

    if (drawing.type === 'pdf' && (!pdfDocument || pdfDocument.url !== drawing.url)) return;
    if (viewport.clientWidth <= 0 || viewport.clientHeight <= 0) return;
    setIsError(false);
    setIsLoading(true);
    const buffer = document.createElement('canvas');

    let renderTask: RenderTask | null = null;
    let image: HTMLImageElement | null = null;
    let isDisposed = false;

    const prepareCanvas = (sourceWidth: number, sourceHeight: number) => {
      const visibleArea = cropArea ? getAreaBounds(cropArea) : { left: 0, top: 0, width: 1, height: 1 };
      const visibleWidth = sourceWidth * visibleArea.width;
      const visibleHeight = sourceHeight * visibleArea.height;
      const scale = Math.min(viewport.clientWidth / visibleWidth, viewport.clientHeight / visibleHeight);
      const width = Math.floor(visibleWidth * scale);
      const height = Math.floor(visibleHeight * scale);
      const pixelRatio = window.devicePixelRatio || 1;
      buffer.width = Math.max(1, Math.floor(width * pixelRatio));
      buffer.height = Math.max(1, Math.floor(height * pixelRatio));
      return { visibleArea, scale, pixelRatio, width, height };
    };

    const commitCanvas = (width: number, height: number) => {
      if (isDisposed) return;
      canvas.width = buffer.width;
      canvas.height = buffer.height;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas unavailable');
      context.drawImage(buffer, 0, 0);
      handleSizeChange(width, height);
      setIsLoading(false);
      setIsError(false);
    };

    const renderPdf = async () => {
      if (drawing.type !== 'pdf' || !pdfDocument) return;
      try {
        const pdfPage = await pdfDocument.document.getPage(page);
        if (isDisposed) return;
        const originalViewport = pdfPage.getViewport({ scale: 1, rotation });
        const { visibleArea, scale, pixelRatio, width, height } = prepareCanvas(originalViewport.width, originalViewport.height);
        const pageViewport = pdfPage.getViewport({ scale, rotation });
        const context = buffer.getContext('2d');
        if (!context) throw new Error('Canvas unavailable');
        renderTask = pdfPage.render({
          canvas: buffer,
          canvasContext: context,
          viewport: pageViewport,
          transform: [
            pixelRatio,
            0,
            0,
            pixelRatio,
            -visibleArea.left * pageViewport.width * pixelRatio,
            -visibleArea.top * pageViewport.height * pixelRatio,
          ],
        });
        await renderTask.promise;
        commitCanvas(width, height);
      } catch (error) {
        if (!isDisposed && error instanceof Error && error.name !== 'RenderingCancelledException') {
          console.error('PDF drawing render failed', error);
          setIsLoading(false);
          setIsError(true);
        }
      }
    };

    const renderImage = () => {
      if (drawing.type !== 'image') return;
      image = new Image();
      image.onload = () => {
        if (isDisposed || !image) return;
        const isQuarterTurn = rotation % 180 !== 0;
        const sourceWidth = isQuarterTurn ? image.naturalHeight : image.naturalWidth;
        const sourceHeight = isQuarterTurn ? image.naturalWidth : image.naturalHeight;
        const { visibleArea, scale, pixelRatio, width, height } = prepareCanvas(sourceWidth, sourceHeight);
        const context = buffer.getContext('2d');
        if (!context) {
          setIsLoading(false);
          setIsError(true);
          return;
        }
        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        context.clearRect(0, 0, buffer.width, buffer.height);
        context.translate(-visibleArea.left * sourceWidth * scale, -visibleArea.top * sourceHeight * scale);
        if (rotation === 90) {
          context.translate(sourceWidth * scale, 0);
          context.rotate(Math.PI / 2);
        } else if (rotation === 180) {
          context.translate(sourceWidth * scale, sourceHeight * scale);
          context.rotate(Math.PI);
        } else if (rotation === 270) {
          context.translate(0, sourceHeight * scale);
          context.rotate(-Math.PI / 2);
        }
        context.drawImage(image, 0, 0, image.naturalWidth * scale, image.naturalHeight * scale);
        commitCanvas(width, height);
      };
      image.onerror = () => {
        if (!isDisposed) {
          setIsLoading(false);
          setIsError(true);
        }
      };
      image.src = drawing.url;
    };

    void renderPdf();
    renderImage();

    return () => {
      isDisposed = true;
      renderTask?.cancel();
      if (image) {
        image.onload = null;
        image.onerror = null;
      }
    };
  }, [cropArea, drawing.url, drawing.type, handleSizeChange, page, pdfDocument, rotation, viewportRef, viewportSize]);

  return (
    <>
      <Canvas ref={canvasRef} style={{ display: isError ? 'none' : 'block', visibility: isLoading ? 'hidden' : 'visible' }} />
      {isError && (
        <TextBlock text='도면을 불러오지 못했습니다.' design={{ text: { size: 'medium', color: color.RED_2 } }} />
      )}
    </>
  );
};

export default DrawingLayer;
